import { LogManager } from '@src/utility/Logger';

import {
  CUSTOMER_TYPE_BY_ID_QUERY,
  CUSTOMER_TYPE_DEFAULT_TEMPLATES_QUERY,
} from '../gql/misc-gql';
import { STORY_QUERY } from '../gql/story-gql';

import type ApiClient from '../apiClient/ApiClient';
import type { AIFlowQueryResult, AIPrompt } from '../types.ts/ai_prompts';
import type { CustomerType, Story } from '../types.ts/story';
import type { Client } from '@datocms/cma-client-browser';
import type { Logger } from '@src/utility/Logger';
import type { GraphQLClient } from 'graphql-request';

interface ResolvedTemplates {
  clipProducerTemplates: AIFlowQueryResult[];
  aiBlogTemplates: AIPrompt[];
  aiSocialPostTemplates: AIPrompt[];
  aiEmailTemplates: AIPrompt[];
  aiShareableImagesTemplates: AIPrompt[];
  aiTalkingPointsTemplate: AIPrompt | null;
  aiImpactScoreTemplate: AIPrompt | null;
  graphicCategories: string[];
  graphicSubcategories: string[];
  videoTemplates: string[];
}

export class TemplatedPromptsRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private logger: Logger;
  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
    this.logger = LogManager.getLogger('repository:TemplatedPromptsRepository');
  }

  async fetchTemplatedPrompts(storyId: string) {
    const storyResponse = await this.gqlClient.request<{
      story: Story;
    }>(STORY_QUERY, { id: storyId });

    const { customerType: defaultTemplates } = await this.gqlClient.request<{
      customerType: CustomerType;
    }>(CUSTOMER_TYPE_DEFAULT_TEMPLATES_QUERY);

    // Apply showcase customer type with inheritance, or fall back to default templates
    const showcase = storyResponse.story._allReferencingShowcases[0];
    let resolvedTemplates: ResolvedTemplates;
    if (showcase?.customerType) {
      // Resolve customer type inheritance chain (not merging with defaults)
      resolvedTemplates = await this.resolveCustomerTypeTemplates(
        showcase.customerType,
      );
    } else {
      resolvedTemplates = {
        clipProducerTemplates: defaultTemplates.clipProducerTemplates || [],
        aiBlogTemplates: defaultTemplates.blogTemplates || [],
        aiSocialPostTemplates: defaultTemplates.socialPostTemplates || [],
        aiEmailTemplates: defaultTemplates.emailTemplates || [],
        aiShareableImagesTemplates:
          defaultTemplates.shareableImagesTemplates || [],
        aiTalkingPointsTemplate: defaultTemplates.talkingPointsTemplate || null,
        aiImpactScoreTemplate: defaultTemplates.impactScoreTemplate || null,
        graphicCategories:
          defaultTemplates.graphicCategories?.map(c => c.id) || [],
        graphicSubcategories:
          defaultTemplates.graphicSubcategories?.map(c => c.id) || [],
        videoTemplates: defaultTemplates.videoTemplates?.map(v => v.id) || [],
      };
    }

    const clipProducerPresets = resolvedTemplates.clipProducerTemplates.map(
      flow => ({
        title: flow.title,
        category: flow.category,
        userPromptDescription: flow.userPromptDescription || '',
      }),
    );

    const result = {
      clipProducerPresets,
      aiBlogTemplates: resolvedTemplates.aiBlogTemplates,
      aiSocialPostTemplates: resolvedTemplates.aiSocialPostTemplates,
      aiEmailTemplates: resolvedTemplates.aiEmailTemplates,
      aiShareableImagesTemplates: resolvedTemplates.aiShareableImagesTemplates,
      aiTalkingPointsTemplate: resolvedTemplates.aiTalkingPointsTemplate,
      aiImpactScoreTemplate: resolvedTemplates.aiImpactScoreTemplate,
      graphicCategories: resolvedTemplates.graphicCategories,
      graphicSubcategories: resolvedTemplates.graphicSubcategories,
      videoTemplates: resolvedTemplates.videoTemplates,
    };
    return result;
  }

  /**
   * Resolves templates from a customer type, applying inheritance from parent customer types.
   * Recursively builds a chain from root to leaf and applies templates respecting override flags.
   */
  private async resolveCustomerTypeTemplates(
    customerType: CustomerType,
  ): Promise<ResolvedTemplates> {
    // Build the inheritance chain from root to leaf
    const chain = await this.buildCustomerTypeChain(customerType);

    // Start with empty templates
    let result: ResolvedTemplates = {
      clipProducerTemplates: [],
      aiBlogTemplates: [],
      aiSocialPostTemplates: [],
      aiEmailTemplates: [],
      aiShareableImagesTemplates: [],
      aiTalkingPointsTemplate: null,
      aiImpactScoreTemplate: null,
      graphicCategories: [],
      graphicSubcategories: [],
      videoTemplates: [],
    };

    // Apply templates from each level in the chain (from root to leaf)

    for (const ct of chain) {
      result = this.applyCustomerTypeTemplates(ct, result);
    }

    return result;
  }

  /**
   * Builds the chain of customer types from root (global) to leaf (specific customer).
   * Also performs circular reference detection to prevent infinite loops.
   */
  private async buildCustomerTypeChain(
    customerType: CustomerType,
  ): Promise<CustomerType[]> {
    const chain: CustomerType[] = [];
    const visitedIds = new Set<string>();
    let current: CustomerType | null = customerType;

    // Build chain from leaf to root, then reverse
    while (current) {
      const parentId: string | undefined = current.parentCustomerType?.id;

      // Circular reference detection
      if (parentId) {
        if (visitedIds.has(parentId)) {
          this.logger.error(
            `Circular reference detected in customer type hierarchy at ID: ${parentId}`,
          );
          break;
        }
        visitedIds.add(parentId);
      }

      chain.unshift(current); // Add to beginning

      // Fetch parent if exists
      if (parentId) {
        try {
          current = await this.fetchCustomerTypeById(parentId);
        } catch (error) {
          this.logger.error(
            `Failed to fetch parent customer type ${parentId}:`,
            error,
          );
          current = null;
        }
      } else {
        current = null;
      }
    }

    return chain;
  }

  /**
   * Fetches a customer type by ID
   */
  private async fetchCustomerTypeById(
    id: string,
  ): Promise<CustomerType | null> {
    try {
      const response = await this.gqlClient.request<{
        customerType: CustomerType;
      }>(CUSTOMER_TYPE_BY_ID_QUERY, { id });
      return response.customerType;
    } catch (error) {
      this.logger.error(`Error fetching customer type ${id}:`, error);
      return null;
    }
  }

  /**
   * Applies templates from a single customer type level.
   * If override is true, only use current level's templates.
   * If override is false, merge current level's templates with parent's.
   */
  private applyCustomerTypeTemplates(
    customerType: CustomerType,
    parentTemplates: ResolvedTemplates,
  ): ResolvedTemplates {
    return {
      clipProducerTemplates: this.mergeTemplates(
        parentTemplates.clipProducerTemplates,
        customerType.clipProducerTemplates,
        customerType.clipProducerOverride,
      ),
      aiBlogTemplates: this.mergeTemplates(
        parentTemplates.aiBlogTemplates,
        customerType.blogTemplates,
        customerType.blogOverride,
      ),
      aiSocialPostTemplates: this.mergeTemplates(
        parentTemplates.aiSocialPostTemplates,
        customerType.socialPostTemplates,
        customerType.socialPostOverride,
      ),
      aiEmailTemplates: this.mergeTemplates(
        parentTemplates.aiEmailTemplates,
        customerType.emailTemplates,
        customerType.emailOverride,
      ),
      aiShareableImagesTemplates: this.mergeTemplates(
        parentTemplates.aiShareableImagesTemplates,
        customerType.shareableImagesTemplates,
        customerType.shareableImagesOverride,
      ),
      aiTalkingPointsTemplate: this.mergeSingleTemplate(
        parentTemplates.aiTalkingPointsTemplate,
        customerType.talkingPointsTemplate,
        customerType.talkingPointsOverride,
      ),
      // No override flag: always prefer this level's template, else inherit the
      // parent's (and ultimately fall back to the global "Impact Score" prompt
      // in getImpactScorePrompt when nothing is set anywhere in the chain).
      aiImpactScoreTemplate: this.mergeSingleTemplate(
        parentTemplates.aiImpactScoreTemplate,
        customerType.impactScoreTemplate,
        undefined,
      ),
      graphicCategories: this.mergeCategories(
        parentTemplates.graphicCategories,
        customerType.graphicCategories,
        customerType.graphicCategoriesOverride,
      ),
      graphicSubcategories: this.mergeCategories(
        parentTemplates.graphicSubcategories,
        customerType.graphicSubcategories,
        customerType.graphicSubcategoriesOverride,
      ),
      videoTemplates: this.mergeCategories(
        parentTemplates.videoTemplates,
        customerType.videoTemplates,
        customerType.videoTemplatesOverride,
      ),
    };
  }

  /**
   * Merges template arrays based on override flag.
   * If override is true: use only current templates (ignore parent)
   * If override is false: add current templates to parent templates
   * Deduplicates by ID for objects with id property
   */
  private mergeTemplates<T extends { id: string }>(
    parentTemplates: T[],
    currentTemplates: T[] | undefined,
    override: boolean | undefined,
  ): T[] {
    if (override === true) {
      return currentTemplates || [];
    } else {
      // Add: merge parent and current templates, deduplicating by ID
      const merged = [...parentTemplates, ...(currentTemplates || [])];

      // Deduplicate by ID if objects have id property
      const seen = new Map<string, T>();
      for (const item of merged) {
        if (item.id) {
          // Use ID as key for deduplication (keep first occurrence)
          if (!seen.has(item.id)) {
            seen.set(item.id, item);
          }
        } else {
          // If no ID, keep all items (shouldn't happen for our types)
          const key = JSON.stringify(item);
          seen.set(key, item);
        }
      }

      return Array.from(seen.values());
    }
  }

  /**
   * Merges single template values based on override flag
   */
  private mergeSingleTemplate<T>(
    parentTemplate: T | null,
    currentTemplate: T | undefined,
    override: boolean | undefined,
  ): T | null {
    if (override === true) {
      // Override: only use current level's template
      return currentTemplate || null;
    } else {
      // Add: prefer current, fallback to parent
      return currentTemplate || parentTemplate;
    }
  }

  /**
   * Merges category arrays, extracting IDs and removing duplicates
   */
  private mergeCategories(
    parentCategories: string[],
    currentCategories: { id: string }[] | undefined,
    override: boolean | undefined,
  ): string[] {
    const currentIds = currentCategories?.map(c => c.id) || [];

    if (override === true) {
      return currentIds;
    } else {
      // Add: merge and deduplicate
      const merged = [...parentCategories, ...currentIds];
      return Array.from(new Set(merged));
    }
  }
}
